import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import { Loader2, Sparkles, TrendingUp, RefreshCw, FlagTriangleRight, AlertCircle, CheckCircle2, AlertTriangle } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const API = process.env.REACT_APP_BACKEND_URL;

const RMAEffectiveness = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/oscar-rules/effectiveness`, { headers });
      setData(res.data);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const createBaseline = async () => {
    setCreating(true);
    try {
      await axios.post(`${API}/api/oscar-rules/baseline`, {}, { headers });
      toast.success("Baseline creado — desde ahora se medirán los nuevos cambios");
      fetchData();
    } catch (e) {
      toast.error("Error creando baseline");
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <Card className="border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-blue-50">
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
        </CardContent>
      </Card>
    );
  }

  if (!data || !data.has_baseline) {
    return (
      <Card data-testid="rma-effectiveness-card" className="border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-blue-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-purple-900">
            <Sparkles className="w-5 h-5" />
            Efectividad RMA
          </CardTitle>
          <CardDescription>
            Crea un baseline para empezar a medir el impacto de tus políticas de Revenue Management.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-600 mb-3">
            ✨ Después de activar tus políticas en Oscar, pulsa el botón para hacer un snapshot del estado actual.
            Desde ese momento, esta tarjeta te mostrará las noches y revenue extra que entran cada día.
          </p>
          <Button
            data-testid="create-baseline-btn"
            onClick={createBaseline}
            disabled={creating}
            className="bg-gradient-to-r from-purple-600 to-blue-600 text-white"
          >
            {creating ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <FlagTriangleRight className="w-4 h-4 mr-2" />
            )}
            Crear baseline (empezar tracking)
          </Button>
        </CardContent>
      </Card>
    );
  }

  const totalRev = data.total_extra_revenue || 0;
  const totalNights = data.total_extra_nights || 0;
  const positive = totalRev >= 0;
  const deltas = data.monthly_deltas || [];
  // Filter only months with delta != 0
  const visible = deltas.filter((d) => d.delta_revenue !== 0 || d.delta_nights !== 0);

  return (
    <Card data-testid="rma-effectiveness-card" className={`border-2 ${positive ? "border-emerald-300" : "border-rose-300"} bg-gradient-to-br ${positive ? "from-emerald-50" : "from-rose-50"} to-white`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className={`w-5 h-5 ${positive ? "text-emerald-600" : "text-rose-600"}`} />
              Efectividad RMA
            </CardTitle>
            <CardDescription>
              Desde el baseline ({data.days_since} días) · {new Date(data.snapshot_date).toLocaleDateString("es-ES")}
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button data-testid="refresh-effectiveness-btn" size="sm" variant="outline" onClick={fetchData}>
              <RefreshCw className="w-3.5 h-3.5" />
            </Button>
            <Button
              data-testid="reset-baseline-btn"
              size="sm"
              variant="outline"
              onClick={createBaseline}
              disabled={creating}
              title="Resetear baseline a ahora"
            >
              <FlagTriangleRight className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Auto Alert */}
        {data.alert && (
          <div
            data-testid="rma-alert"
            className={`p-3 rounded-lg border-2 flex items-start gap-2 ${
              data.alert.color === "rose" ? "bg-rose-50 border-rose-300 text-rose-900" :
              data.alert.color === "amber" ? "bg-amber-50 border-amber-300 text-amber-900" :
              "bg-emerald-50 border-emerald-300 text-emerald-900"
            }`}
          >
            {data.alert.color === "rose" ? <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" /> :
             data.alert.color === "amber" ? <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" /> :
             <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" />}
            <div>
              <div className="font-bold text-sm">{data.alert.title}</div>
              <div className="text-xs mt-0.5">{data.alert.message}</div>
            </div>
          </div>
        )}

        {/* Summary */}
        <div className="grid grid-cols-2 gap-3">
          <div className="p-3 rounded-lg bg-white border">
            <div className="text-xs text-slate-500">Noches extra</div>
            <div className={`text-2xl font-bold font-mono ${positive ? "text-emerald-700" : "text-rose-700"}`}>
              {positive ? "+" : ""}{totalNights}
            </div>
          </div>
          <div className="p-3 rounded-lg bg-white border">
            <div className="text-xs text-slate-500">Revenue extra</div>
            <div className={`text-2xl font-bold font-mono ${positive ? "text-emerald-700" : "text-rose-700"}`}>
              {positive ? "+" : ""}€{Math.round(totalRev).toLocaleString("es-ES")}
            </div>
          </div>
        </div>

        {/* Daily evolution chart */}
        {data.history && data.history.length > 1 && (
          <div data-testid="rma-history-chart" className="bg-white rounded-lg border p-3">
            <div className="text-xs font-semibold text-slate-600 mb-2">📈 Evolución diaria del revenue extra</div>
            <ResponsiveContainer width="100%" height={140}>
              <LineChart data={data.history}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="date" tick={{ fontSize: 9 }} stroke="#64748b" tickFormatter={(d) => d.slice(5)} />
                <YAxis tick={{ fontSize: 9 }} stroke="#64748b" tickFormatter={(v) => `€${Math.round(v/1000)}K`} />
                <Tooltip
                  formatter={(value) => [`€${Math.round(value).toLocaleString()}`, "Revenue extra"]}
                  labelFormatter={(d) => `Día: ${d}`}
                />
                <Line type="monotone" dataKey="total_extra_revenue" stroke={positive ? "#059669" : "#dc2626"} strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Monthly breakdown */}
        {visible.length === 0 ? (
          <div className="text-center text-sm text-slate-500 py-2 italic">
            Aún no hay variaciones. Vuelve mañana para ver los primeros datos.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs sm:text-sm">
              <thead>
                <tr className="border-b text-slate-500">
                  <th className="text-left py-1.5">Mes</th>
                  <th className="text-right py-1.5">Δ noches</th>
                  <th className="text-right py-1.5">Δ revenue</th>
                  <th className="text-right py-1.5">ADR nuevo</th>
                  <th className="text-right py-1.5">Uplift</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((m) => {
                  const dnPos = m.delta_revenue >= 0;
                  return (
                    <tr key={m.month} className="border-b border-slate-100">
                      <td className="py-1.5 font-semibold">{m.month_name}</td>
                      <td className={`py-1.5 text-right font-mono ${dnPos ? "text-emerald-600" : "text-rose-600"}`}>
                        {dnPos ? "+" : ""}{m.delta_nights}
                      </td>
                      <td className={`py-1.5 text-right font-mono ${dnPos ? "text-emerald-600" : "text-rose-600"}`}>
                        {dnPos ? "+" : ""}€{Math.round(m.delta_revenue).toLocaleString("es-ES")}
                      </td>
                      <td className="py-1.5 text-right font-mono text-slate-700">
                        {m.new_adr > 0 ? `€${m.new_adr.toFixed(0)}` : "—"}
                      </td>
                      <td className="py-1.5 text-right">
                        {m.uplift_pct !== 0 ? (
                          <Badge className={m.uplift_pct >= 0 ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}>
                            {m.uplift_pct >= 0 ? "+" : ""}
                            {m.uplift_pct}%
                          </Badge>
                        ) : (
                          <span className="text-slate-400 text-xs">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="text-xs text-slate-500 italic flex items-start gap-1 pt-1 border-t">
          <TrendingUp className="w-3 h-3 flex-shrink-0 mt-0.5" />
          <span>
            Compara revenue actual vs el estado de tu DB cuando creaste el baseline. Resetea con la bandera 🚩 si cambias estrategia.
          </span>
        </div>
      </CardContent>
    </Card>
  );
};

export default RMAEffectiveness;
